import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RefundDetailsModal from './components/RefundDetailsModal';
import ApprovalActionModal from './components/ApprovalActionModal';
import BulkApprovalModal from './components/BulkApprovalModal';

const RefundManagementCenter = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [amountFilter, setAmountFilter] = useState('all');
  const [selectedRefunds, setSelectedRefunds] = useState([]);
  const [refundDetailsModal, setRefundDetailsModal] = useState({ isOpen: false, refund: null });
  const [approvalModal, setApprovalModal] = useState({ isOpen: false, refund: null, action: null });
  const [bulkModal, setBulkModal] = useState({ isOpen: false });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [refunds, setRefunds] = useState([
    {
      id: 1,
      requestId: 'REF-2026-001',
      customerName: 'Acme Corp',
      customerEmail: 'contact@acmecorp.com',
      transactionId: 'TXN-2026-001234',
      originalAmount: 14999,
      refundAmount: 14999,
      reasonCode: 'Service Cancellation',
      reason: 'Client cancelled subscription within refund period',
      requestDate: '2026-02-07 10:30:00',
      priority: 'High',
      status: 'Pending',
      attachments: ['cancellation_request.pdf'],
      customerHistory: {
        totalPurchases: 45000,
        refundHistory: 0,
        accountAge: '2 years'
      },
      disputeInfo: null,
      assignedTo: 'Customer Service'
    },
    {
      id: 2,
      requestId: 'REF-2026-002',
      customerName: 'Tech Solutions Ltd',
      customerEmail: 'admin@techsolutions.com',
      transactionId: 'TXN-2026-001180',
      originalAmount: 5999,
      refundAmount: 2999,
      reasonCode: 'Partial Service Issue',
      reason: 'Features not working as expected for 2 weeks',
      requestDate: '2026-02-06 14:15:00',
      priority: 'Medium',
      status: 'Pending',
      attachments: ['bug_report.pdf', 'screenshots.zip'],
      customerHistory: {
        totalPurchases: 18000,
        refundHistory: 1,
        accountAge: '1 year'
      },
      disputeInfo: {
        disputeId: 'DSP-001',
        filedDate: '2026-02-05'
      },
      assignedTo: 'Finance Team'
    },
    {
      id: 3,
      requestId: 'REF-2026-003',
      customerName: 'StartupXYZ',
      customerEmail: 'founder@startupxyz.com',
      transactionId: 'TXN-2026-001150',
      originalAmount: 2999,
      refundAmount: 2999,
      reasonCode: 'Duplicate Charge',
      reason: 'Customer was charged twice for the same subscription',
      requestDate: '2026-02-05 09:45:00',
      priority: 'High',
      status: 'Approved',
      approvedBy: 'John Doe',
      approvedDate: '2026-02-05 11:00:00',
      processedDate: '2026-02-05 11:15:00',
      attachments: ['payment_proof.pdf'],
      customerHistory: {
        totalPurchases: 8997,
        refundHistory: 0,
        accountAge: '6 months'
      },
      disputeInfo: null,
      assignedTo: 'Management'
    },
    {
      id: 4,
      requestId: 'REF-2026-004',
      customerName: 'Digital Ventures',
      customerEmail: 'billing@digitalventures.com',
      transactionId: 'TXN-2026-001090',
      originalAmount: 2999,
      refundAmount: 2999,
      reasonCode: 'Service Not Delivered',
      reason: 'Premium features were not activated after payment',
      requestDate: '2026-02-04 16:20:00',
      priority: 'Low',
      status: 'Rejected',
      rejectedBy: 'Jane Smith',
      rejectedDate: '2026-02-05 10:30:00',
      rejectionReason: 'Features were activated. User error confirmed.',
      attachments: [],
      customerHistory: {
        totalPurchases: 5998,
        refundHistory: 2,
        accountAge: '8 months'
      },
      disputeInfo: null,
      assignedTo: 'Customer Service'
    },
    {
      id: 5,
      requestId: 'REF-2026-005',
      customerName: 'Global Enterprises',
      customerEmail: 'finance@globalent.com',
      transactionId: 'TXN-2026-001050',
      originalAmount: 14999,
      refundAmount: 7499,
      reasonCode: 'Downgrade Request',
      reason: 'Client wants to downgrade and receive prorated refund',
      requestDate: '2026-02-03 13:00:00',
      priority: 'Medium',
      status: 'Pending',
      attachments: ['downgrade_request.pdf'],
      customerHistory: {
        totalPurchases: 60000,
        refundHistory: 0,
        accountAge: '3 years'
      },
      disputeInfo: null,
      assignedTo: 'Finance Team'
    }
  ]);

  const filteredRefunds = refunds?.filter(refund => {
    const matchesTab = activeTab === 'pending' ? refund?.status === 'Pending' :
                       activeTab === 'approved' ? refund?.status === 'Approved' :
                       activeTab === 'rejected' ? refund?.status === 'Rejected' : true;
    const matchesSearch = refund?.customerName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         refund?.requestId?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         refund?.customerEmail?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesPriority = priorityFilter === 'all' || refund?.priority === priorityFilter;
    const matchesAmount = amountFilter === 'all' ||
                         (amountFilter === 'low' && refund?.refundAmount < 3000) ||
                         (amountFilter === 'medium' && refund?.refundAmount >= 3000 && refund?.refundAmount < 10000) ||
                         (amountFilter === 'high' && refund?.refundAmount >= 10000);
    return matchesTab && matchesSearch && matchesPriority && matchesAmount;
  });

  const totalPages = Math.ceil(filteredRefunds?.length / itemsPerPage);
  const paginatedRefunds = filteredRefunds?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectRefund = (refundId) => {
    setSelectedRefunds(prev =>
      prev?.includes(refundId) ? prev?.filter(id => id !== refundId) : [...prev, refundId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRefunds?.length === paginatedRefunds?.filter(r => r?.status === 'Pending')?.length) {
      setSelectedRefunds([]);
    } else {
      setSelectedRefunds(paginatedRefunds?.filter(r => r?.status === 'Pending')?.map(r => r?.id));
    }
  };

  const handleApprovalAction = (refund, action) => {
    setApprovalModal({ isOpen: true, refund, action });
  };

  const handleBulkApproval = () => {
    if (selectedRefunds?.length > 0) {
      setBulkModal({ isOpen: true });
    }
  };

  const confirmApproval = (refundId, action, data) => {
    setRefunds(prev => prev?.map(refund => {
      if (refund?.id === refundId) {
        if (action === 'approve') {
          return {
            ...refund,
            status: 'Approved',
            approvedBy: 'Current Admin',
            approvedDate: new Date()?.toISOString(),
            refundAmount: data?.amount || refund?.refundAmount,
            internalNotes: data?.notes
          };
        } else if (action === 'reject') {
          return {
            ...refund,
            status: 'Rejected',
            rejectedBy: 'Current Admin',
            rejectedDate: new Date()?.toISOString(),
            rejectionReason: data?.reason
          };
        }
      }
      return refund;
    }));
    setApprovalModal({ isOpen: false, refund: null, action: null });
  };

  const confirmBulkApproval = (action) => {
    setRefunds(prev => prev?.map(refund => {
      if (selectedRefunds?.includes(refund?.id)) {
        return {
          ...refund,
          status: action === 'approve' ? 'Approved' : 'Rejected',
          approvedBy: action === 'approve' ? 'Current Admin' : undefined,
          rejectedBy: action === 'reject' ? 'Current Admin' : undefined,
          approvedDate: action === 'approve' ? new Date()?.toISOString() : undefined,
          rejectedDate: action === 'reject' ? new Date()?.toISOString() : undefined
        };
      }
      return refund;
    }));
    setSelectedRefunds([]);
    setBulkModal({ isOpen: false });
  };

  const getStatusBadge = (status) => {
    const styles = {
      Pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      Approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      Rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return styles?.[status] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      High: 'bg-red-500/20 text-red-400 border-red-500/30',
      Medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      Low: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return styles?.[priority] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  const stats = {
    pending: refunds?.filter(r => r?.status === 'Pending')?.length,
    approved: refunds?.filter(r => r?.status === 'Approved')?.length,
    rejected: refunds?.filter(r => r?.status === 'Rejected')?.length,
    totalAmount: refunds?.filter(r => r?.status === 'Pending')?.reduce((sum, r) => sum + r?.refundAmount, 0)
  };

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
      }`}>
        {/* Header */}
        <header className="bg-slate-900 border-b border-slate-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white font-heading">
                Refund Management Center
              </h1>
              <p className="text-sm text-slate-400 mt-1 font-body">
                Process refund requests with approval workflows
              </p>
            </div>
            <Button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex"
              variant="outline"
            >
              <Icon name={sidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
            </Button>
          </div>
        </header>

        {/* Stats Cards */}
        <div className="px-6 py-4 bg-slate-900/50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 font-body">Pending Requests</p>
                  <p className="text-2xl font-bold text-yellow-400 mt-1 font-heading">{stats?.pending}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Icon name="Clock" size={24} color="#FBBF24" />
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 font-body">Approved</p>
                  <p className="text-2xl font-bold text-green-400 mt-1 font-heading">{stats?.approved}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} color="#4ADE80" />
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 font-body">Rejected</p>
                  <p className="text-2xl font-bold text-red-400 mt-1 font-heading">{stats?.rejected}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Icon name="XCircle" size={24} color="#F87171" />
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 font-body">Total Pending Amount</p>
                  <p className="text-2xl font-bold text-amber-400 mt-1 font-heading">₹{stats?.totalAmount?.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Icon name="DollarSign" size={24} color="#F59E0B" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto px-6 py-6">
          {/* Tabs */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex space-x-2">
              {['pending', 'approved', 'rejected']?.map(tab => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    activeTab === tab
                      ? 'bg-amber-500 text-white' :'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {tab?.charAt(0)?.toUpperCase() + tab?.slice(1)} Requests
                </button>
              ))}
            </div>
            {selectedRefunds?.length > 0 && (
              <Button onClick={handleBulkApproval} className="bg-amber-500 hover:bg-amber-600">
                <Icon name="CheckSquare" size={18} />
                <span className="ml-2">Bulk Actions ({selectedRefunds?.length})</span>
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
                  Search
                </label>
                <div className="relative">
                  <Icon name="Search" size={18} color="#94A3B8" className="absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    placeholder="Search by name, ID, email..."
                    className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
                  Priority
                </label>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e?.target?.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="all">All Priorities</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
                  Amount Range
                </label>
                <select
                  value={amountFilter}
                  onChange={(e) => setAmountFilter(e?.target?.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="all">All Amounts</option>
                  <option value="low">Below ₹3,000</option>
                  <option value="medium">₹3,000 - ₹10,000</option>
                  <option value="high">Above ₹10,000</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={() => {
                    setSearchQuery('');
                    setPriorityFilter('all');
                    setAmountFilter('all');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  <Icon name="RotateCcw" size={18} />
                  <span className="ml-2">Reset Filters</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Refunds Table */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    {activeTab === 'pending' && (
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedRefunds?.length === paginatedRefunds?.filter(r => r?.status === 'Pending')?.length && paginatedRefunds?.filter(r => r?.status === 'Pending')?.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Request ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Reason
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Priority
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {paginatedRefunds?.length > 0 ? (
                    paginatedRefunds?.map((refund) => (
                      <tr key={refund?.id} className="hover:bg-slate-700/30 transition-colors">
                        {activeTab === 'pending' && (
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={selectedRefunds?.includes(refund?.id)}
                              onChange={() => handleSelectRefund(refund?.id)}
                              className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
                            />
                          </td>
                        )}
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-white font-body">{refund?.requestId}</div>
                          <div className="text-xs text-slate-400 font-caption">{new Date(refund.requestDate)?.toLocaleDateString()}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-white font-body">{refund?.customerName}</div>
                          <div className="text-xs text-slate-400 font-caption">{refund?.customerEmail}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-white font-body">₹{refund?.refundAmount?.toLocaleString()}</div>
                          <div className="text-xs text-slate-400 font-caption">Original: ₹{refund?.originalAmount?.toLocaleString()}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-white font-body">{refund?.reasonCode}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityBadge(refund?.priority)}`}>
                            {refund?.priority}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(refund?.status)}`}>
                            {refund?.status}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setRefundDetailsModal({ isOpen: true, refund })}
                              className="p-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                              title="View Details"
                            >
                              <Icon name="Eye" size={16} color="#94A3B8" />
                            </button>
                            {refund?.status === 'Pending' && (
                              <>
                                <button
                                  onClick={() => handleApprovalAction(refund, 'approve')}
                                  className="p-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-colors"
                                  title="Approve"
                                >
                                  <Icon name="CheckCircle" size={16} color="#4ADE80" />
                                </button>
                                <button
                                  onClick={() => handleApprovalAction(refund, 'reject')}
                                  className="p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-colors"
                                  title="Reject"
                                >
                                  <Icon name="XCircle" size={16} color="#F87171" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <Icon name="Inbox" size={48} color="#475569" />
                          <p className="mt-4 text-slate-400 font-body">No refund requests found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
                <div className="text-sm text-slate-400 font-body">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredRefunds?.length)} of {filteredRefunds?.length} results
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    className="px-3 py-1"
                  >
                    <Icon name="ChevronLeft" size={16} />
                  </Button>
                  {[...Array(totalPages)]?.map((_, idx) => (
                    <button
                      key={idx + 1}
                      onClick={() => setCurrentPage(idx + 1)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === idx + 1
                          ? 'bg-amber-500 text-white' :'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    className="px-3 py-1"
                  >
                    <Icon name="ChevronRight" size={16} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      {/* Modals */}
      {refundDetailsModal?.isOpen && (
        <RefundDetailsModal
          refund={refundDetailsModal?.refund}
          onClose={() => setRefundDetailsModal({ isOpen: false, refund: null })}
        />
      )}
      {approvalModal?.isOpen && (
        <ApprovalActionModal
          refund={approvalModal?.refund}
          action={approvalModal?.action}
          onConfirm={(data) => confirmApproval(approvalModal?.refund?.id, approvalModal?.action, data)}
          onClose={() => setApprovalModal({ isOpen: false, refund: null, action: null })}
        />
      )}
      {bulkModal?.isOpen && (
        <BulkApprovalModal
          selectedCount={selectedRefunds?.length}
          onConfirm={confirmBulkApproval}
          onClose={() => setBulkModal({ isOpen: false })}
        />
      )}
    </div>
  );
};

export default RefundManagementCenter;